import {Box, HBox} from '../../components/core/view';
import {HomeScreens, ShopperScreens} from '@buncha/home/navigation/types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  useGetConstants,
  useGetStoreCategories,
  useItemDetailsReducer,
} from '../hooks/ItemDetails';

import AdjOOSStatus from '@buncha/batchDetails/component/locateItemList/AdjOOSStatus';
import {Analytics} from '@buncha/config/analytics/analytics';
import B2BLocationInStore from '../components/ItemDetail/B2BLocationInStore';
import {BackHeaderLeft} from '@buncha/common/components/BackHeaderLeft';
import {EventType} from '@buncha/config/analytics/type';
import Header from '../../components/composites/Header/Header';
import If from '../../components/conditional/If';
import ItemDetailsBody from '../components/ItemDetail/ItemDetailsBody';
import {ItemDetailsContext} from '../context/ItemDetails';
import {ItemDetailsScreen} from '../navigation/types';
import LocationInStore from '../components/ItemDetail/locationInStore';
import {Modules} from '@buncha/utils/types';
import {OrderItemStatus} from '@buncha/batchDetails/types';
import {getClientType} from '@buncha/common/types/common';
import {isWeb} from '@buncha/utils/common';
import {navigation} from '@buncha/config/navigation';
import {smartLook} from '@buncha/config/analytics';
import {styles} from '../styles/ItemDetails';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useCheckHasPriceTag} from '../hooks/ItemScanner';
import {useColorThemeType} from '../../appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useRoute} from '@react-navigation/native';
import {useUpdateOrderItemStatus} from '@buncha/batchDetails/hooks/BatchDetails';

interface RouteParamsType {
  storeIndex?: string;
  catalogItemId?: number;
  orderItemId?: number;
  runId?: number;
  isUPCMode?: boolean;
  storeId?: number;
}

const ItemDetailsPage = () => {
  const theme = useColorThemeType();
  const [state, dispatch] = useItemDetailsReducer();
  const provider = useMemo(() => ({state, dispatch}), [dispatch, state]);
  const {getConstants, constantsLoading} = useGetConstants(dispatch);
  const {getStoreCategories} = useGetStoreCategories(dispatch);
  const [storeMasterId, setStoreMasterId] = useState<number | undefined>(
    undefined,
  );
  const route = useRoute();

  const WAIT_TIME_FOR_AOOS = useAppSelector(
    gState => gState.onboarding.constants?.WAIT_TIME_FOR_AOOS,
  );
  const routeParams: RouteParamsType | undefined = route?.params;
  const orderItemId = Number(routeParams?.orderItemId) || 0;
  const isUPCMode = routeParams?.isUPCMode;
  const storeIndex = routeParams?.storeIndex;
  const runId = routeParams?.runId ?? '';
  const storeId = routeParams?.storeId;
  const catalogItemId = routeParams?.catalogItemId ?? '';
  const isMeijerStore = useIsMeijerStore(storeId);
  const {button} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const [loadingData, setLoadingData] = useState(false);
  const {checkHasPriceTag, hasPriceTagCheck} = useCheckHasPriceTag();
  const {updateOrderItemStatus} = useUpdateOrderItemStatus();

  const foundAt = state?.item?.aoosData?.adjacentOOS?.foundAt;

  const quantity =
    state.item?.totalupdatedQuantityByShopper ?? state.item?.quantity;

  const bottomButtonLoading = loadingData || constantsLoading;

  const minutes = parseInt(WAIT_TIME_FOR_AOOS ?? '0', 10) ?? 10;
  const dateWithOffset = new Date(
    (foundAt ? new Date(foundAt) : new Date()).getTime() + minutes * 60 * 1000,
  );

  const isCatalogItem = state.item?.id !== orderItemId;

  const hasScannerEnabled = useMemo(() => {
    return isCatalogItem && !isWeb();
  }, [isCatalogItem]);

  const getButtonText = useMemo(() => {
    if (hasScannerEnabled) return button?.foundItem;
    return button?.pickItem;
  }, [button?.foundItem, button?.pickItem, hasScannerEnabled]);

  const navigateToScanPage = useCallback(() => {
    navigation.navigate(ShopperScreens.ItemDetails, {
      screen: ItemDetailsScreen.ItemScanner,
      params: {
        orderItemId,
        storeIndex,
        runId,
        quantity,
        isUpcMode: isUPCMode,
        source: 'Item Detail Page',
        catalogItemId: catalogItemId,
      },
    });
  }, [catalogItemId, isUPCMode, orderItemId, quantity, runId, storeIndex]);

  const navigateToBatchScreen = useCallback(() => {
    navigation.navigate(HomeScreens.Shopper, {
      screen: ShopperScreens.BatchDetailsPage,
      params: {runId},
    });
  }, [runId]);

  const onPress = useCallback(() => {
    const eventProps = {
      type: EventType.cta,
      runID: runId,
      client: getClientType(isMeijerStore),
      orderItemId,
      catalogItemId,
      storeIndex,
    };
    Analytics.eventWithProps('Found Item', eventProps);
    smartLook.trackCustomEvent('Found Item', eventProps);
    if (!hasScannerEnabled && runId && isWeb() && !isMeijerStore)
      updateOrderItemStatus(
        runId,
        OrderItemStatus.picked,
        [orderItemId],
        navigateToBatchScreen,
      );
    else if (state?.item?.isNSC2Item)
      navigation.navigate(ShopperScreens.ItemDetails, {
        screen: ItemDetailsScreen.NSCItemScanner,
        params: {
          orderItemId,
          storeIndex,
          runId,
          catalogItemId,
        },
      });
    else
      navigation.navigate(ShopperScreens.ItemDetails, {
        screen: ItemDetailsScreen.ItemScanner,
        params: {
          orderItemId,
          storeIndex,
          runId,
          quantity,
          orderItemIds: state?.orderItemIds,
          storeMasterId: storeMasterId,
          hasPriceTagAlreadyVerified: hasPriceTagCheck,
          source: 'Item Detail Page',
        },
      });
  }, [
    catalogItemId,
    hasPriceTagCheck,
    hasScannerEnabled,
    isMeijerStore,
    navigateToBatchScreen,
    orderItemId,
    quantity,
    runId,
    state?.item?.isNSC2Item,
    state?.orderItemIds,
    storeIndex,
    storeMasterId,
    updateOrderItemStatus,
  ]);

  const handleFoundItem = useCallback(
    (sMasterId?: number) => {
      setStoreMasterId(sMasterId);
      if (isUPCMode && hasScannerEnabled) navigateToScanPage();
      else onPress();
    },
    [hasScannerEnabled, isUPCMode, navigateToScanPage, onPress],
  );

  const handleLoadingData = useCallback((loading: boolean) => {
    setLoadingData(loading);
  }, []);

  useEffect(() => {
    if (storeIndex) getStoreCategories(storeIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeIndex]);

  useEffect(() => {
    getConstants();
    if (catalogItemId && storeIndex)
      checkHasPriceTag(catalogItemId, storeIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ItemDetailsContext.Provider value={provider}>
      <Header
        showDefaultLeftHeader={false}
        hideBottomSafeArea
        topComponent={
          <If condition={state?.item?.aoosData?.shopperStatus}>
            <Box
              style={[
                styles.aoosStatus,
                {
                  backgroundColor: theme?.background?.blue?.[200],
                },
              ]}>
              <AdjOOSStatus
                foundAt={dateWithOffset}
                status={state?.item?.aoosData?.shopperStatus}
                shopper2={state?.item?.aoosData?.newShopper?.firstName}
              />
            </Box>
          </If>
        }
        headerRightComponent={
          <HBox
            style={[styles.header, {backgroundColor: theme?.text.blue[300]}]}>
            <If condition={!isMeijerStore}>
              <BackHeaderLeft
                containerStyle={{backgroundColor: theme?.text.blue[300]}}
                iconColor={theme?.text.white[100]}
              />
              <If condition={!bottomButtonLoading && state.item}>
                <LocationInStore
                  category={state.item?.shopperCategory || 'Custom Item'}
                />
              </If>
            </If>
            <If condition={isMeijerStore}>
              <B2BLocationInStore
                storeId={storeId}
                location={state.item?.itemLocation}
              />
            </If>
          </HBox>
        }
        headerColor={theme?.background.blue[300]}
        style={{backgroundColor: theme?.background?.gray?.[200]}}>
        <Box style={styles.mainContainer}>
          <ItemDetailsBody
            isUPCMode={isUPCMode}
            setLoading={handleLoadingData}
            loading={constantsLoading}
            bottomButtonLoading={bottomButtonLoading}
            onButtonPress={handleFoundItem}
            buttonText={getButtonText}
            hasPriceTag={hasPriceTagCheck}
          />
        </Box>
      </Header>
    </ItemDetailsContext.Provider>
  );
};

export default React.memo(ItemDetailsPage);
